import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Popover } from 'react-tiny-popover';
import EventCard from '../../components/EventCard';
import { CalendarDateRange } from '../../components/DateCalendar';
import { useApp } from '../../context/AppContext';
import { useEventCache } from '../../context/EventCacheContext';
import Select from '../../components/Select';
import { mapBy } from '../../../../../../../../shared/lib/functional';
import { evaluateAudienceZone } from '../../../../../../../../shared/lib/audience';
import { ReservableEventTemplate } from '../../../../../../../../shared/types/reservable';
import Button from '../../components/Button';
import Pagination from '../../components/Pagination';
import { FullScreenLoader } from '../../components/FullScreenLoader';
import Alert from '../../components/Alert';
import DateCalendar from '../../components/DateCalendar';
import { dateToIsoLocal, getLocaleDateStringLocal } from '../../../../../../../../shared/lib/time';

interface GridViewProps {
  filtersTargetRef: HTMLDivElement;
  onSelectEvent: (event: ReservableEventTemplate) => void;
}

export default function GridView({ filtersTargetRef, onSelectEvent }: GridViewProps) {
  const { dynamicConstants, config } = useApp();
  const { isMobile } = dynamicConstants;
  const { allEvents, setEventDateRange, loading } = useEventCache();

  const [selectedRange, setSelectedRange] = useState<CalendarDateRange | undefined>(undefined);
  const [selectedSegment, setSelectedSegments] = useState<string | undefined>(undefined);
  const [selectedZone, setSelectedZone] = useState<string | undefined>(undefined);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);

  const dataItems = allEvents?.eventTemplates ?? [];
  const dataCollections = allEvents?.collections ?? {
    segments: [],
    levels: [],
    attributes: [],
    zones: [],
  };

  const segmentMap = mapBy(dataCollections.segments, s => s._id);

  const segmentOptions = dataCollections.segments.map(seg => seg.name);
  const zoneOptions = dataCollections.zones.map(z => z.name);

  const filteredItems = dataItems.filter(items => {
    if (selectedSegment) {
      const seg = dataCollections.segments.find(s => s.name === selectedSegment);
      if (!seg) return false;
      if (items.segment !== seg._id) return false;
    }

    if (selectedZone) {
      const zone = dataCollections.zones.find(z => z.name === selectedZone);
      if (!zone) return false;
      if (!evaluateAudienceZone('visible', zone._id, undefined, items.audience)) return false;
    }

    return true;
  });

  const itemsPerPage = 12;
  const start = currentPage * itemsPerPage;

  const paginatedItems = filteredItems.slice(start, start + itemsPerPage);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const gridDiv = useRef<HTMLDivElement>(null);

  const portalDiv = (
    <div ref={gridDiv} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-start">
      <div className="min-w-0">
        {/* @ts-ignore */}
        <Popover
          isOpen={isCalendarOpen}
          positions={['bottom', 'top']}
          boundaryElement={gridDiv.current || undefined}
          parentElement={gridDiv.current || undefined}
          onClickOutside={() => setIsCalendarOpen(false)}
          containerStyle={{ zIndex: '10' }}
          content={
            <DateCalendar
              selectedRange={selectedRange}
              onRangeSelect={range => {
                setSelectedRange(range);
                if (range.from && range.to) {
                  setEventDateRange(dateToIsoLocal(range.from), dateToIsoLocal(range.to));
                  setIsCalendarOpen(false);
                }
              }}
              onClear={() => {
                setSelectedRange(undefined);
                setEventDateRange(undefined, undefined);
                setIsCalendarOpen(false);
              }}
              className="shadow-lg border border-table"
              showClear
              showToday
            />
          }
        >
          <div>
            <Button
              variant="white"
              size="small"
              block
              onClick={() => setIsCalendarOpen(prev => !prev)}
              className={`border-border-i ${!selectedRange?.from && 'text-muted-i'}`}
            >
              <span className="flex-grow text-left font-normal">
                {selectedRange?.from && selectedRange?.to
                  ? `${getLocaleDateStringLocal(selectedRange.from, { month: 'short', day: 'numeric' })} – ${getLocaleDateStringLocal(selectedRange.to, { month: 'short', day: 'numeric', year: 'numeric' })}`
                  : selectedRange?.from
                    ? `${getLocaleDateStringLocal(selectedRange.from, { month: 'short', day: 'numeric' })} – ...`
                    : `${config.dateFilterLabel}...`}
              </span>
            </Button>
          </div>
        </Popover>
      </div>

      <div className="min-w-0">
        <Select
          name="segment"
          aria-label="Filter by segment..."
          options={segmentOptions}
          value={selectedSegment}
          onValue={val => setSelectedSegments(val)}
          emptyLabel={`${config.segmentFilterLabel}...`}
        />
      </div>

      <div className="min-w-0">
        <Select
          name="audience"
          aria-label="Filter by audience..."
          options={zoneOptions}
          value={selectedZone}
          onValue={val => setSelectedZone(val)}
          emptyLabel={`${config.audienceFilterLabel}...`}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-white p-6 relative">
      {loading ? (
        <FullScreenLoader />
      ) : filteredItems.length === 0 ? (
        <Alert>No experiences match your filters. Try adjusting your selections.</Alert>
      ) : (
        <>
          <div
            className={`grid gap-4 grid-cols-1 ${isMobile ? '' : 'md:grid-cols-2 lg:grid-cols-3'}`}
          >
            {paginatedItems.map(t => {
              const segmentName = t.segment
                ? (segmentMap[t.segment]?.name ?? undefined)
                : undefined;

              const visibleZones = dataCollections.zones.filter(z =>
                evaluateAudienceZone('visible', z._id, undefined, t.audience)
              );

              return (
                <EventCard
                  key={t._id}
                  template={t}
                  tag={segmentName}
                  zones={visibleZones}
                  onBook={() => onSelectEvent(t)}
                />
              );
            })}
          </div>

          <Pagination currentPage={currentPage} totalPages={totalPages} onPage={setCurrentPage} />
        </>
      )}

      {createPortal(portalDiv, filtersTargetRef)}
    </div>
  );
}
